/**
 * FacetMultiSelectDropdown
 */

import React, { useEffect, useRef } from 'react';
import { AggregationsModel } from 'types';
import '../../utils/facet-multi-select-dropdown';
import type { ChangeDetail } from '../../utils/facet-multi-select-dropdown';

interface Props {
	/** The filters for the component*/
	filters: AggregationsModel[];

	/** Callback for on filter change */
	onChange?: any;

	/** Clear text for filters */
	clearText?: string;

	/** Text for expand button in dropdown */
	expandText?: string;

	/** Show / hide search field inside dropdown */
	searchable?: boolean;

	/** Show / hide selected chips */
	showChips?: boolean;
}

type FacetMultiSelectDropdownElementInstance = HTMLElement & {
	filters: AggregationsModel[];
	clearText: string;
	expandText: string;
	searchable: boolean;
	showChips: boolean;
};

const DEFAULT_EXPAND_TEXT = 'Expandera';
const DEFAULT_CLEAR_TEXT = 'Rensa';
const FacetMultiSelectDropdownElementTag = 'facet-multi-select-dropdown' as any;

const FacetMultiSelectDropdown: React.FC<Props> = ({
	filters: defaultFilters,
	onChange,
	clearText,
	expandText,
	searchable = true,
	showChips = true,
}) => {
	const elementRef = useRef<FacetMultiSelectDropdownElementInstance | null>(
		null
	);

	useEffect(() => {
		const element = elementRef.current;

		if (!element) {
			return;
		}

		element.filters = defaultFilters;
		element.clearText = clearText || DEFAULT_CLEAR_TEXT;
		element.expandText = expandText || DEFAULT_EXPAND_TEXT;
		element.searchable = searchable;
		element.showChips = showChips;
	}, [clearText, defaultFilters, expandText, searchable, showChips]);

	useEffect(() => {
		const element = elementRef.current;

		if (!element || !onChange) {
			return;
		}

		const handleChange = (event: Event) => {
			const detail = (event as CustomEvent<ChangeDetail>).detail;
			onChange(detail.filters, detail.query);
		};

		element.addEventListener(
			'facet-multi-select-dropdown-change',
			handleChange as EventListener
		);

		return () => {
			element.removeEventListener(
				'facet-multi-select-dropdown-change',
				handleChange as EventListener
			);
		};
	}, [onChange]);

	return (
		<FacetMultiSelectDropdownElementTag
			ref={elementRef}
			clear-text={clearText || DEFAULT_CLEAR_TEXT}
			expand-text={expandText || DEFAULT_EXPAND_TEXT}
			show-chips={showChips ? 'true' : 'false'}
		/>
	);
};

export default FacetMultiSelectDropdown;
